import React, {
	createContext,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { usePostOfficeContext } from '@atlassian/post-office-context/post-office-context';
import { usePlacementFetchingContext } from '@atlassian/post-office-placement-data/placement-fetcher-provider';

import { useAnalytics } from '@townsquare/analytics';
import Cache from '@townsquare/cache';
import { fg } from '@townsquare/stat-sig/gate';

import { getFeatureId } from '../utils/get-feature-id';

import { type FetchDependencies, fetchMessages } from './fetchPlacementMessages';
import type { PlacementMessages } from './types';

// In memory for the page session, not local storage: a message can be registered to several placements and dismissed once
export const MESSAGES_TTL_MS: number = 60 * 60 * 1000;

type SharedPlacementMessages = {
	load: (placementId: string) => Promise<PlacementMessages>;
	dismiss: (featureId: string) => void;
};

type SharedPlacementMessagesProviderProps = { children: React.ReactNode };

const toContextKey = (context: object): string =>
	JSON.stringify(Object.entries(context).sort(([a], [b]) => a.localeCompare(b)));

const SharedPlacementMessagesContext = createContext<SharedPlacementMessages | undefined>(
	undefined,
);

export type UseSharedPlacementMessagesResult = {
	isLoading: boolean;
	messages?: PlacementMessages;
	dismiss: (featureId: string) => void;
};

export const useSharedPlacementMessages = (
	placementId: string,
	isEnabled: boolean,
): UseSharedPlacementMessagesResult | undefined => {
	const placementMessagesResult = useContext(SharedPlacementMessagesContext);
	const [state, setState] = useState<{ isLoading: boolean; messages?: PlacementMessages }>({
		isLoading: true,
	});

	useEffect(() => {
		if (!isEnabled || !placementMessagesResult) {
			return;
		}

		let isCurrent = true;
		placementMessagesResult.load(placementId).then(
			(messages) => {
				if (isCurrent) {
					setState({ isLoading: false, messages });
				}
			},
			() => {
				if (isCurrent) {
					setState({ isLoading: false });
				}
			},
		);
		return () => {
			isCurrent = false;
		};
	}, [isEnabled, placementMessagesResult, placementId]);

	return isEnabled && placementMessagesResult
		? { ...state, dismiss: placementMessagesResult.dismiss }
		: undefined;
};

const SharedPlacementMessagesProviderImpl = ({
	children,
}: SharedPlacementMessagesProviderProps) => {
	const postOfficeContext = usePostOfficeContext();
	const fetcher = usePlacementFetchingContext();
	const analytics = useAnalytics();

	// The store below is created once, so it reads these through a ref to use the current values without being recreated
	// Layout effects run before children's useEffect loads, so new mounts see the current context
	const latest = useRef<FetchDependencies>({ postOfficeContext, fetcher, analytics });
	useLayoutEffect(() => {
		latest.current = { postOfficeContext, fetcher, analytics };
	}, [postOfficeContext, fetcher, analytics]);

	// Stable initialisation: runs once per provider, so there is one store for its lifetime and no setter
	const [placementMessagesResult] = useState<SharedPlacementMessages>(() => {
		const cache = new Cache<Promise<PlacementMessages>>();
		// The cached response still contains dismissed messages, so filter them here until Post Office omits them on the next fetch
		const dismissedFeatureIds = new Set<string>();

		const withoutDismissed = (messages: PlacementMessages): PlacementMessages =>
			messages.filter((message) => {
				const featureId = getFeatureId(message);
				return !featureId || !dismissedFeatureIds.has(featureId);
			});

		const load = (placementId: string): Promise<PlacementMessages> => {
			const key = `${placementId}:${toContextKey(latest.current.postOfficeContext)}`;
			const cached = cache.get(key);
			if (cached) {
				return cached.then(withoutDismissed);
			}

			const request = fetchMessages(placementId, latest.current);
			cache.set(key, request, MESSAGES_TTL_MS);
			request.catch(() => {
				// Cache has no delete; a negative ttl expires the entry immediately
				if (cache.get(key) === request) {
					cache.set(key, request, -1);
				}
			});
			return request.then(withoutDismissed);
		};

		return {
			load,
			dismiss: (featureId) => {
				dismissedFeatureIds.add(featureId);
			},
		};
	});

	return (
		<SharedPlacementMessagesContext.Provider value={placementMessagesResult}>
			{children}
		</SharedPlacementMessagesContext.Provider>
	);
};

/**
 * Fetches Post Office messages once per placement and shares them with the wrappers below it.
 * With `townsquare_enable_messages_as_placement_props` off it renders only its children.
 * The store keeps each response in memory for an hour and remembers dismissed features.
 * The context carries the store itself and no data, so consumers do not re-render when messages load.
 */
export const SharedPlacementMessagesProvider = ({
	children,
}: SharedPlacementMessagesProviderProps): React.JSX.Element =>
	fg('townsquare_enable_messages_as_placement_props') ? (
		<SharedPlacementMessagesProviderImpl>{children}</SharedPlacementMessagesProviderImpl>
	) : (
		<>{children}</>
	);
